import React, { FC, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { PurpleString } from '../algorithmCondition/coloredLabels/PurpleString';
import translations from './AlgorithmTargets.translations.json';
import { Flex } from 'antd';
import styled from 'styled-components';
import {
  colorBgContainer,
  fontWeightStrong,
} from '@atlas-frontend/design-tokens';
import { Table } from '../../../common/tanstacktable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AlgorithmTargetRuleType } from '../../../dataobject/types';
import { getAlgorithmSourceShortLabel } from '../utils';
import { TextEllipsis } from '../../../common/components';
import { getMapBy } from '../../../common/utils';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import {
  AlgorithmTargetItemFormDataType,
  AlgorithmTargetRuleFormDataType,
} from './schema/types';
import { AlgorithmCondition } from '../algorithmCondition/AlgorithmCondition';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-targets');
addTranslations(translations);

const columnHelper = createColumnHelper<AlgorithmTargetRuleFormDataType>();

export interface AlgorithmTargetRulesProps {
  rules: AlgorithmTargetRuleFormDataType[];
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  isLoading?: false | true | undefined;
}

export const AlgorithmTargetRules: FC<AlgorithmTargetRulesProps> = (props) => {
  const { rules, sources, mappings, isLoading } = props;
  const { t } = useTranslation();

  const sourcesById = useMemo(
    () => getMapBy(sources.filter(Boolean), 'id'),
    [sources],
  );

  const columns: ColumnDef<AlgorithmTargetRuleFormDataType, any>[] =
    useMemo(() => {
      return [
        columnHelper.accessor('ruleCode', {
          header: t('ruleCode.label'),
          enableColumnFilter: false,
          enableSorting: false,
          meta: {
            width: 40,
          },
        }),
        columnHelper.accessor('ruleType', {
          header: t('ruleType.label'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => t('ruleType.' + props.renderValue()),
          meta: {
            width: 40,
          },
        }),
        columnHelper.accessor('mappingRule' as any, {
          header: t('mappingRule.label'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => {
            const rule = props.row.original;
            if (rule.ruleType === AlgorithmTargetRuleType.RULE) {
              return (
                <TextEllipsis tooltip={rule.expression || ''}>
                  <AlgorithmCondition
                    condition={rule.expression || ''}
                    mappings={mappings || []}
                  />
                </TextEllipsis>
              );
            } else if (rule.algorithmSource) {
              return `${getAlgorithmSourceShortLabel(rule.algorithmSource)}.${
                rule.sourceItemCode
              }`;
            } else if (rule.algorithmMapping) {
              return <PurpleString>{rule.algorithmMapping.alias}</PurpleString>;
            } else if (rule.algorithmMappingId) {
              return (
                mappings.find(
                  (mapping) => mapping.id === rule.algorithmMappingId,
                )?.alias ?? ''
              );
            } else if (rule.sourceItemCode) {
              return rule.sourceItemCode;
            }
          },
        }),
        columnHelper.accessor('items', {
          header: t('items.label'),
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => {
            const codeNames = (props.row.original.items || []).map(
              (item: AlgorithmTargetItemFormDataType) => {
                if (item.indicatorCode) {
                  if (item.confidentialityStatus) {
                    return `${t('items.conf', {
                      code: item.indicatorCode,
                    })} - ${item.indicatorName}`;
                  }
                  return `${item.indicatorCode} - ${item.indicatorName}`;
                } else {
                  return `${item.dimensionCode} - ${item.dimensionName}`;
                }
              },
            );
            return codeNames.join(', ');
          },
          meta: {
            width: 220,
          },
        }),
      ];
    }, [sourcesById, mappings, t]);

  return (
    <Flex vertical wrap="wrap" gap="small">
      <TableWrapper id="target-rules-view-table-wrapper">
        <Table
          name="target-rules-view-table"
          data={rules || []}
          isLoading={isLoading}
          columns={columns}
          sticky
          scrollContainerSelector="#target-rules-view-table-wrapper"
        />
      </TableWrapper>
    </Flex>
  );
};

const TableWrapper = styled.div`
  overflow: auto;
  max-height: 400px;
  font-weight: ${fontWeightStrong};

  th {
    background-color: ${colorBgContainer};

    &::before {
      display: none;
    }
  }

  tr > td {
    box-shadow: none;
  }
`;
